import React from "react"
import { Box, debounce, Slider, TextField, Typography } from "@material-ui/core"
import { min as getMin, max as getMax, isUndefined } from "lodash"
import { SearchQueryParameter } from "src/useSearchParameters"

function isNumeric(num: any) {
  return !isNaN(num)
}

interface ValueRangeTagGroupProps {
  name: string
  range: [number, number]
  currentSelection: [number | undefined, number | undefined]
  minType: SearchQueryParameter
  maxType: SearchQueryParameter
  valueSymbol?: string
  tagCounts: Record<string, number>
  onFilterSelected: (
    type: SearchQueryParameter,
    value: number | undefined
  ) => void
}

export const ValueRangeTagGroup: React.FC<ValueRangeTagGroupProps> = ({
  name,
  range,
  minType,
  maxType,
  valueSymbol,
  currentSelection,
  onFilterSelected,
}) => {
  const [min, max] = range
  const [value, setValue] = React.useState(currentSelection)
  const [textInputMin, setTextInputMin] = React.useState<string>(
    `${currentSelection[0] || ""}`
  )
  const [textInputMax, setTextInputMax] = React.useState<string>(
    `${currentSelection[1] || ""}`
  )

  const onMinChanged = React.useCallback(
    debounce((value?: number) => onFilterSelected(minType, value), 500),
    [onFilterSelected]
  )

  const onMaxChanged = React.useCallback(
    debounce((value?: number) => onFilterSelected(maxType, value), 500),
    [onFilterSelected]
  )

  const updateValue = (
    newValue: [number | undefined, number | undefined],
    notify = false
  ) => {
    const [curMin, curMax] = currentSelection
    const newMin = newValue[0] && newValue[0] <= min ? undefined : newValue[0]
    const newMax = newValue[1] && newValue[1] >= max ? undefined : newValue[1]

    setValue(newValue)
    setTextInputMin(`${newMin || ""}`)
    setTextInputMax(`${newMax || ""}`)

    if (notify) {
      if (newMin !== curMin) onMinChanged(newMin)
      if (newMax !== curMax) onMaxChanged(newMax)
    }
  }

  const handleRangeInputChange = (_: any, newValue: [number, number]) => {
    updateValue(newValue)
  }

  const handleMinTextInputChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (isNumeric(evt.target.value) || evt.target.value === "") {
      setTextInputMin(evt.target.value)
    }
  }

  const handleMaxTextInputChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (isNumeric(evt.target.value) || evt.target.value === "") {
      setTextInputMax(evt.target.value)
    }
  }

  const handleChangeCommitted = (
    _event: object,
    newValue: [number, number]
  ) => {
    updateValue(newValue, true)
  }

  const handleFormSubmit = (evt: React.FormEvent<HTMLElement>) => {
    evt.preventDefault()

    const vMin = Number.parseInt(textInputMin) || undefined
    const vMax = Number.parseInt(textInputMax) || undefined

    if (isUndefined(vMin) || isUndefined(vMax)) {
      updateValue([vMin, vMax], true)
    } else {
      updateValue([getMin([vMin, vMax]), getMax([vMin, vMax])], true)
    }
  }

  const valuetext = (n: number) => {
    return n === min || n === max ? "n/a" : `${n}${valueSymbol}`
  }

  return (
    <>
      <Typography id={`${name}-title`} variant="subtitle2" component="h2">
        {name}
      </Typography>
      <Box marginX={2}>
        <Slider
          value={[value[0] ?? min, value[1] ?? max]}
          min={min}
          max={max}
          onChange={handleRangeInputChange}
          valueLabelDisplay="auto"
          aria-labelledby={`${name}-title`}
          getAriaValueText={valuetext}
          valueLabelFormat={valuetext}
          onChangeCommitted={handleChangeCommitted}
        />
      </Box>
      <Box display="flex">
        <Box component="form" onSubmit={handleFormSubmit}>
          <TextField
            value={textInputMin}
            variant="outlined"
            size="small"
            onChange={handleMinTextInputChange}
            type="text"
          />
        </Box>
        <Box
          display="flex"
          marginX={1}
          justifyContent="center"
          alignItems="center"
          fontWeight="fontWeightBold"
          fontSize={16}
        >
          {"-"}
        </Box>
        <Box component="form" onSubmit={handleFormSubmit}>
          <TextField
            value={textInputMax}
            variant="outlined"
            size="small"
            onChange={handleMaxTextInputChange}
          />
        </Box>
      </Box>
    </>
  )
}
